import React, { useContext, useState } from 'react';
import { Button, Grid, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box } from '@mui/material';
import { IoInformationCircle } from 'react-icons/io5';
import { useNavigate, useLocation } from 'react-router-dom';
import { LanguageContext } from '../../../../../../libs/Context';
import { updateFeedApprovalStatus } from '../../../../../actions/feeds_actions_new';
import { toast } from 'react-toastify';
import useFeedApproval from '../../FeedCreation/CustomHooks/useFeedApproval';
import { feedActionContext } from '../../../..';

interface ApprovalStatusProps {
	feed: any;
	handleUpdateFeed: (update: any) => void;
}

const ApprovalStatus: React.FC<ApprovalStatusProps> = ({ feed, handleUpdateFeed }) =>
{
	const { isFeedsAdmin } = useFeedApproval();
	const { language } = useContext(LanguageContext);
	const [ openRejectDialog, setOpenRejectDialog ] = useState(false);
	const [ rejectionReason, setRejectionReason ] = useState('');
	const { handleFeedAction } = useContext(feedActionContext);
	const navigate = useNavigate();
	const location = useLocation();

	const handleStatusUpdate = async (status: 'approved' | 'rejected', reason?: string) =>
	{
		const isApproving = status === 'approved';
		const messages = {
			success : isApproving
				? (language?.feedApproved || 'Feed approved successfully')
				: (language?.feedRejected || 'Feed rejected'),
			error : isApproving
				? (language?.feedApprovalFailed || 'Failed to approve feed')
				: (language?.feedRejectionFailed || 'Failed to reject feed')
		};

		try
		{
			let body = {};

			if (!isApproving)
			{
				body = { reason };
			}
			const res = await updateFeedApprovalStatus(feed._id, status, body);

			if (res?.status)
			{
				if (isApproving)
				{
					// remove feed from feed list
					handleFeedAction('append', 'remove_feed', feed);
				}
				else
				{
					// update feed status in feed list
					handleUpdateFeed({
						status,
						...(reason && { rejectionReason: reason })
					});
				}
				toast.success(messages.success);
				if (!isApproving)
				{
					setOpenRejectDialog(false);
					setRejectionReason('');
				}
				const urlParams = new URLSearchParams(location.search);

				if (location.pathname === '/news' && urlParams.has('post'))
				{
					navigate('/news');
				}
			}
			else
			{
				toast.error(messages.error);
			}
		}
		catch (err)
		{
			console.error(err);
			toast.error(messages.error);
		}
	};

	const MAX_REASON_LENGTH = 250;

	const handleReject = () =>
	{
		const trimmedReason = rejectionReason.trim();

		if (!trimmedReason)
		{
			toast.error(language?.rejectionReasonRequired || 'Please provide a reason for rejection');

			return;
		}
		if (trimmedReason.length > MAX_REASON_LENGTH)
		{
			toast.error(language?.rejectionReasonTooLong || `Rejection reason cannot exceed ${MAX_REASON_LENGTH} characters`);

			return;
		}
		handleStatusUpdate('rejected', trimmedReason);
	};

	const PendingOrRejectedComponent = () =>
	{
		const statusColor = feed.status === 'rejected'
			? 'error'
			: feed.status === 'approved'
				? 'success.main'
				: 'warning.main';

		return (
			<Box sx={{ display: 'flex', alignItems: 'start', gap: 1, width: '100%' }} component='span'>
				<Box sx={{ display: 'flex', width: '24px', height: '24px' }}>
					<IoInformationCircle
						size={20}
						color={feed.status === 'rejected' ? '#d32f2f' : feed.status === 'approved' ? '#2e7d32' : '#ed6c02'}
					/>
				</Box>
				<Box sx={{ minWidth: '60px', display: 'flex', alignItems: 'center' }}>
					<Typography
						variant='body2'
						color={statusColor}
						sx={{ display: 'flex', alignItems: 'center' }}
					>
						{feed.status === 'rejected'
							? `${language?.feedRejectedMessage || 'Rejected'}: `
							: feed.status === 'approved'
								? (language?.feedApprovedMessage || 'Approved')
								: (language?.feedPendingMessage || 'Pending Approval')}
					</Typography>
				</Box>
				<Box sx={{ display: 'flex' }}>
					{feed.status === 'rejected' && (
						<Typography variant='body2'>
							{feed?.rejectionReason || ''}
						</Typography>
					)}
				</Box>
			</Box>
		);
	};

	if (isFeedsAdmin)
	{
		return (
			<>
				<Grid container spacing={2} alignItems='center'>
					<Grid item xs>
						{feed.status === 'rejected' && <PendingOrRejectedComponent />}
					</Grid>
					{feed?.status === 'pending' && <Grid item>
						<Button
							variant='outlined'
							// color='error'
							onClick={() => setOpenRejectDialog(true)}
						>
							{language?.reject || 'Reject'}
						</Button>
					</Grid>}
					{feed?.status === 'pending' && <Grid item>
						<Button
							variant='contained'
							sx={{ color: 'var(--whiteColor)' }}
							onClick={() => handleStatusUpdate('approved')}
						>
							{language?.approve || 'Approve'}
						</Button>
					</Grid>}
				</Grid>
				<Dialog
					open={openRejectDialog}
					onClose={() => setOpenRejectDialog(false)}
					maxWidth='sm'
					fullWidth
				>
					<DialogTitle>
						{language?.rejectFeedTitle || 'Reject Feed'}
					</DialogTitle>
					<DialogContent>
						<TextField
							autoFocus
							margin='dense'
							label={language?.rejectionReason || 'Rejection Reason'}
							fullWidth
							multiline
							rows={4}
							value={rejectionReason}
							onChange={(e) => setRejectionReason(e.target.value)}
							placeholder={language?.rejectionReasonPlaceholder || 'Please provide a reason for rejecting this feed...'}
							inputProps={{ maxLength: MAX_REASON_LENGTH }}
							helperText={`${rejectionReason.length}/${MAX_REASON_LENGTH}`}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setOpenRejectDialog(false)}>
							{language?.cancel || 'Cancel'}
						</Button>
						<Button onClick={handleReject} sx={{ color: 'var(--whiteColor)' }} variant='contained'>
							{language?.reject || 'Reject'}
						</Button>
					</DialogActions>
				</Dialog>
			</>
		);
	}

	return (
		<PendingOrRejectedComponent />
	);
};

export default ApprovalStatus;
